import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { capitalizeWord, isClientSide, useFeatureFlag } from '@moda/portal-stanchions';
import { colorInHex } from '@moda/tokens';
import { CONFIG } from '../../config';
import { useIsCloudFrontViewerCountryUS } from '../../hooks/useIsCloudfrontViewerCountryUS';
import { useUser } from '../../hooks/useUser';
import {
  getZendeskWidgetAuthToken,
  hideZendeskWidget,
  identifyZendeskUser,
  isCustomerCareChatDepartmentOnline,
  isPCAsChatDepartmentOnline,
  isZendeskWidgetLoaded,
  observeZendeskLauncher,
  observeZendeskWidget,
  onCustomerCareChatDepartmentStatusChange,
  onPCAsChatDepartmentStatusChange,
  onZendeskLauncherClick,
  prefillZendeskUserDetails,
  setZendeskLauncherObserver,
  setZendeskWidgetDefaultLocale,
  setZendeskWidgetObserver,
  showZendeskWidget,
  styleZendeskLauncherForOfflineSupport,
  styleZendeskLauncherForOnlineStylingSupport,
  styleZendeskLauncherForOnlineSupport,
  unobserveZendeskLauncher,
  unobserveZendeskWidget,
  updateZendeskWidgetPath,
  updateZendeskWidgetSettings
} from '../../lib/zendesk/widget';
import { tc } from '../../lib/trackingContext';
import {
  ZendeskDepartments,
  ZendeskSettings,
  ZendeskTrackEvent,
  ZendeskTrackEventSource
} from '../../types/zendesk.d';
import { ProductDetailsFragment as Variant } from '../../generated/types';
import { ClientSideOnly } from '../ClientSideOnly';

const ONE_SECOND = 1000;

interface ZendeskWidgetProps {
  context?: { variant?: Variant };
  serveToPCAs?: boolean;
  showAnswerBot?: boolean;
  showChat?: boolean;
  showContactForm?: boolean;
  showHelpCenter?: boolean;
  source?: ZendeskTrackEventSource;
}

export const ZendeskWidget: React.FC<ZendeskWidgetProps> = props => {
  return (
    <ClientSideOnly>
      <ZendeskWidgetClient {...props} />
    </ClientSideOnly>
  );
};

const ZendeskWidgetClient: React.FC<ZendeskWidgetProps> = ({
  context,
  serveToPCAs = false,
  showAnswerBot = false,
  showChat = false,
  showContactForm = false,
  showHelpCenter = false,
  source
}) => {
  const [isLoaded, setIsLoaded] = useState<boolean>(false);

  const [arePCAsOnline, setArePCAsOnline] = useState<boolean>(false);
  const [isCustomerCareOnline, setIsCustomerCareOnline] = useState<boolean>(false);

  const zendeskWidgetAuthFlag = useFeatureFlag('zendesk-widget-auth', [
    { default: false },
    { if: true, then: true }
  ]);

  const zendeskWidgetLocationRestriction = useFeatureFlag('zendesk-widget-location-restriction', [
    { default: false },
    { if: true, then: true }
  ]);

  const hasBeenPreviouslyServedToPCAs =
    isClientSide() && window.zESettings
      ? window.zESettings.webWidget?.chat?.departments?.select ===
        ZendeskDepartments.PrivateClientAdvisors
      : serveToPCAs;

  const isCloudFrontViewerCountryUS = useIsCloudFrontViewerCountryUS();
  const { user } = useUser();

  const isChatAvailable: boolean = useMemo((): boolean => {
    if (showChat) {
      if (serveToPCAs) {
        if (arePCAsOnline) return true;
      } else if (isCustomerCareOnline) return true;
    }

    return false;
  }, [arePCAsOnline, isCustomerCareOnline, serveToPCAs, showChat]);

  const isChatBeingServedByADifferentDepartment = useMemo(
    () => serveToPCAs != hasBeenPreviouslyServedToPCAs,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [serveToPCAs]
  );

  const settings: ZendeskSettings = useMemo(
    (): ZendeskSettings => ({
      webWidget: {
        answerBot: {
          suppress: !(showAnswerBot && showHelpCenter)
        },
        ...(user.isLoggedIn && zendeskWidgetAuthFlag
          ? {
              authenticate: {
                chat: {
                  jwtFn: async (callback: (token: string | null) => void) => {
                    const token = await getZendeskWidgetAuthToken();
                    if (token != null) callback(token);
                  }
                }
              }
            }
          : {}),
        chat: {
          departments: {
            select: serveToPCAs
              ? ZendeskDepartments.PrivateClientAdvisors
              : ZendeskDepartments.CustomerCare
          },
          hideWhenOffline: true,
          suppress: !showChat,
          ...(serveToPCAs
            ? {
                concierge: {
                  name: 'Expert Advice',
                  title: { '*': 'Chat with a Stylist' }
                },
                title: {
                  '*': 'Chat with a Stylist'
                }
              }
            : {
                concierge: {
                  name: 'Customer Care',
                  title: { '*': 'Chat live with a representative' }
                },
                title: {
                  '*': 'Chat live with us'
                }
              })
        },
        color: {
          articleLinks: colorInHex('klein-blue'),
          button: colorInHex('ink'),
          header: colorInHex('ink'),
          launcher: colorInHex('ink'),
          launcherText: colorInHex('snow'),
          resultLists: colorInHex('ink'),
          theme: colorInHex('ink')
        },
        contactForm: {
          suppress: !showContactForm
        },
        helpCenter: {
          suppress: !showHelpCenter
        }
      }
    }),
    [
      serveToPCAs,
      showAnswerBot,
      showChat,
      showContactForm,
      showHelpCenter,
      user,
      zendeskWidgetAuthFlag
    ]
  );

  const track = useCallback(
    (event: ZendeskTrackEvent) => {
      if (event != null && source != null)
        tc.track(`${source} ${event}`, {
          addToScopeOnce: {
            ...(context?.variant != null ? { variant: context.variant } : {}),
            user
          },
          addToTrackingEvent: {
            label: context?.variant?.id != null ? context.variant.id : user?.id || null
          }
        });
    },
    [context, source, user]
  );

  const styleLauncher = useCallback(
    (show = false): void => {
      if (show) {
        if (serveToPCAs) styleZendeskLauncherForOnlineStylingSupport();
        else styleZendeskLauncherForOnlineSupport();
      } else styleZendeskLauncherForOfflineSupport();
    },
    [serveToPCAs]
  );

  const restyleLauncher = useCallback(() => {
    updateZendeskWidgetPath();
    styleLauncher(isChatAvailable);
  }, [isChatAvailable, styleLauncher]);

  const initialize = useCallback((): void => {
    setZendeskWidgetDefaultLocale();

    onCustomerCareChatDepartmentStatusChange((status: boolean) => {
      setIsCustomerCareOnline(status);
    });

    onPCAsChatDepartmentStatusChange((status: boolean) => {
      setArePCAsOnline(status);
    });

    styleLauncher(showChat);

    hideZendeskWidget();
  }, [setArePCAsOnline, setIsCustomerCareOnline, showChat, styleLauncher]);

  const observe = useCallback((): void => {
    unobserveZendeskLauncher();
    unobserveZendeskWidget();

    setZendeskLauncherObserver(
      () => setIsLoaded(true),
      () => track(ZendeskTrackEvent.ChatUsed)
    );
    setZendeskWidgetObserver(() => track(ZendeskTrackEvent.ChatUsed));

    observeZendeskLauncher();
    observeZendeskWidget();
  }, [setIsLoaded, track]);

  useEffect(() => {
    observe();
  }, [observe]);

  useEffect(() => {
    if (isLoaded) {
      initialize();
    }
  }, [initialize, isLoaded]);

  useEffect(() => {
    updateZendeskWidgetSettings(settings);
  }, [settings]);

  useEffect(() => {
    if (isChatAvailable) {
      setTimeout(showZendeskWidget, ONE_SECOND);
      track(ZendeskTrackEvent.ChatDisplayed);
    } else {
      hideZendeskWidget();
    }
  }, [isChatAvailable, track]);

  useEffect(() => {
    setIsCustomerCareOnline(isCustomerCareChatDepartmentOnline());
    setArePCAsOnline(isPCAsChatDepartmentOnline());

    onCustomerCareChatDepartmentStatusChange((status: boolean) => {
      setIsCustomerCareOnline(status);
    });

    onPCAsChatDepartmentStatusChange((status: boolean) => {
      setArePCAsOnline(status);
    });
  }, [setArePCAsOnline, setIsCustomerCareOnline]);

  useLayoutEffect(() => {
    if (isChatBeingServedByADifferentDepartment) {
      onZendeskLauncherClick(() => track(ZendeskTrackEvent.ChatUsed));
      hideZendeskWidget();
      setTimeout(restyleLauncher, ONE_SECOND);
    }
  }, [isChatBeingServedByADifferentDepartment, restyleLauncher, track]);

  useEffect(() => {
    if (isZendeskWidgetLoaded()) {
      if (user?.firstName != null && user?.email != null) {
        const fullName = `${capitalizeWord(user.firstName)}${
          user.lastName != null ? ` ${capitalizeWord(user.lastName)}` : ''
        }`;

        prefillZendeskUserDetails({
          email: {
            value: user.email
          },
          name: {
            value: fullName
          },
          ...(user.phone != null
            ? {
                phone: {
                  value: user.phone
                }
              }
            : {})
        });

        identifyZendeskUser({
          email: user.email || '',
          name: fullName || ''
        });
      }
    }
  }, [isLoaded, user]);

  if (!((showAnswerBot && showHelpCenter) || showChat || showContactForm || showHelpCenter))
    return null;

  if (
    zendeskWidgetLocationRestriction &&
    !(isCloudFrontViewerCountryUS || user.countryCode === 'US')
  )
    return null;

  return !isLoaded ? (
    <Helmet>
      <script
        id="ze-snippet"
        src={`https://static.zdassets.com/ekr/snippet.js?key=${CONFIG.ZENDESK_WIDGET_KEY}`}
      />
    </Helmet>
  ) : null;
};
